import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DateSelector from './DateSelector';
import LoveResults from './LoveResults'; // We'll keep the file name but change the content inside
import AchievementsDisplay from './AchievementsDisplay';
import MilestonesDisplay from './MilestonesDisplay';
import CollapsibleSection from './CollapsibleSection';
import { calculateDaysDifference, calculateDetailedDifference } from '../utils/dateUtils';
import { PET_ACHIEVEMENTS, PET_MILESTONES, PET_QUOTES } from '../data/loveData';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { parseUrlParams } from '../utils/shareUtils';

const PetCalculator = ({ language, onDataUpdate }) => {
  const [anniversaryDate, setAnniversaryDate] = useState(null);
  const [anniversaryType, setAnniversaryType] = useState('adoption');
  const [petType, setPetType] = useState('dog');
  const [customLabel, setCustomLabel] = useState('');
  const [customLabelEn, setCustomLabelEn] = useState('');
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [resultsVisible, setResultsVisible] = useState(false);
  const [showAllMilestones, setShowAllMilestones] = useState(false);
  const [petName, setPetName] = useState('');

  // Extract URL parameters for SEO-friendly deep linking
  useEffect(() => {
    // Use the utility function to parse URL parameters
    const { date, type, petType: urlPetType, petName: urlPetName, language: urlLanguage } = parseUrlParams();
    
    // Set date if provided in URL
    if (date) {
      setAnniversaryDate(date);
      
      // Auto-calculate when date is provided in URL
      setTimeout(() => {
        handleCalculate(date);
      }, 300);
    }
    
    // Set type if provided in URL
    if (type) {
      setAnniversaryType(type);
    }

    // Set pet type if provided in URL
    if (urlPetType) {
      setPetType(urlPetType);
    }

    // Set pet name if provided in URL
    if (urlPetName) {
      setPetName(urlPetName);
    }
  }, []);

  const handleCalculate = (date = anniversaryDate) => {
    if (!date) return;

    const today = new Date();
    const days = calculateDaysDifference(date, today);
    
    if (days < 0) {
      alert(language === 'zh' ? '紀念日期不能是未來的日期' : 'Anniversary date cannot be in the future');
      return;
    }

    setCalculatedDays(days);
    setResultsVisible(true);
    
    // Update URL with date, type, pet type and pet name for shareable links without page reload
    const relationshipLabel = getRelationshipLabel();
    const newUrl = new URL(window.location);
    newUrl.searchParams.set('date', `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`);
    newUrl.searchParams.set('type', anniversaryType);
    newUrl.searchParams.set('petType', petType);
    if (petName) {
      newUrl.searchParams.set('petName', petName);
    }
    newUrl.searchParams.set('lang', language);
    window.history.replaceState({}, '', newUrl);
    
    // Pass data to parent for SEO optimization
    if (onDataUpdate) {
      onDataUpdate({
        anniversaryDate: date,
        anniversaryType,
        petType,
        petName,
        calculatedDays: days,
        relationshipLabel
      });
    }
  };

  const getRelationshipLabel = () => {
    if (anniversaryType === 'custom') {
      return language === 'zh' 
        ? (customLabel || '紀念') 
        : (customLabelEn || 'commemorating');
    }

    const labels = {
      adoption: { zh: '領養', en: 'adoption' },
      birthday: { zh: '出生', en: 'birth' },
      'first-meet': { zh: '相識', en: 'meeting' },
      'first-home': { zh: '回家', en: 'coming home' },
      'first-training': { zh: '訓練', en: 'training' }
    };

    return labels[anniversaryType]?.[language] || labels.adoption[language];
  };

  const getRandomQuote = () => {
    const quotes = PET_QUOTES[language] || [];
    return quotes[Math.floor(Math.random() * quotes.length)];
  };
  
  // Get anniversary type icon
  const getAnniversaryTypeIcon = (type) => {
    const icons = {
      'adoption': '🏠',
      'birthday': '🎂',
      'first-meet': '👋',
      'first-home': '🛌',
      'first-training': '🎓',
      'custom': '✨'
    };
    return icons[type] || '🏠';
  };

  // Get pet type icon
  const getPetTypeIcon = (type) => {
    const icons = {
      'dog': '🐶',
      'cat': '🐱',
      'bird': '🐦',
      'rabbit': '🐰',
      'hamster': '🐹',
      'fish': '🐠',
      'turtle': '🐢',
      'other': '🐾'
    };
    return icons[type] || '🐾';
  };

  const achievements = PET_ACHIEVEMENTS[language] || [];
  const detailedTime = anniversaryDate ? calculateDetailedDifference(anniversaryDate, new Date()) : null;

  // Define the anniversary types with icons and descriptions for enhanced UI
  const anniversaryTypes = [
    {
      id: 'adoption',
      icon: '🏠',
      nameZh: '領養日',
      nameEn: 'Adoption Day',
      descriptionZh: '記錄寵物正式成為家庭成員的日子',
      descriptionEn: 'Commemorate the day your pet officially became part of your family'
    },
    {
      id: 'birthday',
      icon: '🎂',
      nameZh: '生日',
      nameEn: 'Birthday',
      descriptionZh: '慶祝寵物的出生日期',
      descriptionEn: 'Celebrate your pet\'s birth date'
    },
    {
      id: 'first-meet',
      icon: '👋',
      nameZh: '初次相遇',
      nameEn: 'First Meeting',
      descriptionZh: '紀念您與寵物第一次見面的時刻',
      descriptionEn: 'Commemorate the moment you first met your pet'
    },
    {
      id: 'first-home',
      icon: '🛌',
      nameZh: '回家日',
      nameEn: 'Homecoming Day',
      descriptionZh: '記錄寵物第一天到家的日子',
      descriptionEn: 'Remember the first day your pet came home'
    },
    {
      id: 'first-training',
      icon: '🎓',
      nameZh: '訓練日',
      nameEn: 'Training Day',
      descriptionZh: '記錄寵物開始訓練的日子',
      descriptionEn: 'Mark the day your pet started training'
    },
    {
      id: 'custom',
      icon: '✨',
      nameZh: '自定義紀念日',
      nameEn: 'Custom',
      descriptionZh: '創建屬於您和寵物的特別紀念日',
      descriptionEn: 'Create your own special commemoration with your pet'
    }
  ];

  // Define pet types with icons and descriptions
  const petTypes = [
    {
      id: 'dog',
      icon: '🐶',
      nameZh: '狗狗',
      nameEn: 'Dog'
    },
    {
      id: 'cat',
      icon: '🐱',
      nameZh: '貓咪',
      nameEn: 'Cat'
    },
    {
      id: 'bird',
      icon: '🐦',
      nameZh: '鳥鳥',
      nameEn: 'Bird'
    },
    {
      id: 'rabbit',
      icon: '🐰',
      nameZh: '兔兔',
      nameEn: 'Rabbit'
    },
    {
      id: 'hamster',
      icon: '🐹',
      nameZh: '倉鼠',
      nameEn: 'Hamster'
    },
    {
      id: 'fish',
      icon: '🐠',
      nameZh: '魚兒',
      nameEn: 'Fish'
    },
    {
      id: 'turtle',
      icon: '🐢',
      nameZh: '烏龜',
      nameEn: 'Turtle'
    },
    {
      id: 'other',
      icon: '🐾',
      nameZh: '其他寵物',
      nameEn: 'Other Pet'
    }
  ];

  return (
    <div className="p-6 bg-gradient-to-br from-white to-love-50 dark:from-gray-800 dark:to-gray-850">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Date Selection */}
        <motion.div 
          className="space-y-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Pet Name Input */}
          <motion.div 
            className="bg-white dark:bg-gray-750 p-4 rounded-lg border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
          >
            <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300 flex items-center">
              <span className="emoji mr-2">{getPetTypeIcon(petType)}</span>
              <span className="text-base">{language === 'zh' ? '寵物名字' : 'Pet Name'}</span>
            </label>
            <input
              type="text"
              value={petName}
              onChange={(e) => setPetName(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border-2 border-love-300 dark:border-love-600 bg-white dark:bg-gray-700 text-base focus:outline-none focus:ring-2 focus:ring-love-500 dark:focus:ring-love-400 transition-colors"
              placeholder={language === 'zh' ? '請輸入寵物名字...' : 'Enter your pet\'s name...'}
              aria-label={language === 'zh' ? '寵物名字' : 'Pet Name'}
            />
          </motion.div>

          <DateSelector
            language={language}
            selectedDate={anniversaryDate}
            onDateChange={setAnniversaryDate}
            label={language === 'zh' ? '寵物紀念日' : 'Pet Anniversary'}
            icon="🐾"
          />

          {/* Pet Type Selector */}
          <motion.div 
            className="bg-white dark:bg-gray-750 p-4 rounded-lg border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
          >
            <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300 flex items-center">
              <span className="emoji mr-2">{getPetTypeIcon(petType)}</span>
              <span className="text-base">{language === 'zh' ? '寵物類型' : 'Pet Type'}</span>
            </label>
            
            {/* Visual Card-based Pet Type Selection */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {petTypes.map(type => (
                <motion.div
                  key={type.id}
                  onClick={() => {
                    setPetType(type.id);
                    // Update URL when pet type changes
                    const newUrl = new URL(window.location);
                    newUrl.searchParams.set('petType', type.id);
                    window.history.replaceState({}, '', newUrl);
                  }}
                  className={`cursor-pointer p-3 rounded-lg border-2 transition-all ${
                    petType === type.id 
                      ? 'border-love-500 bg-love-50 dark:bg-love-900/30 dark:border-love-400 shadow-sm' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-love-300 dark:hover:border-love-700'
                  }`}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label={language === 'zh' ? type.nameZh : type.nameEn}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex flex-col items-center text-center">
                    <span className="emoji text-2xl mb-1">{type.icon}</span>
                    <span className={`text-sm font-medium ${
                      petType === type.id 
                        ? 'text-love-700 dark:text-love-300' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {language === 'zh' ? type.nameZh : type.nameEn}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Enhanced Anniversary Type Selector */}
          <motion.div 
            className="bg-white dark:bg-gray-750 p-4 rounded-lg border border-love-100 dark:border-love-900 shadow-sm hover:shadow-md transition-shadow"
            whileHover={{ scale: 1.01 }}
            transition={{ duration: 0.2 }}
          >
            <label className="block text-sm font-medium mb-3 text-gray-700 dark:text-gray-300 flex items-center">
              <span className="emoji mr-2">{getAnniversaryTypeIcon(anniversaryType)}</span>
              <span className="text-base">{language === 'zh' ? '紀念類型' : 'Anniversary Type'}</span>
            </label>
            
            {/* Visual Card-based Type Selection */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
              {anniversaryTypes.map(type => (
                <motion.div
                  key={type.id}
                  onClick={() => {
                    setAnniversaryType(type.id);
                    // Update URL when type changes
                    const newUrl = new URL(window.location);
                    newUrl.searchParams.set('type', type.id);
                    window.history.replaceState({}, '', newUrl);
                  }}
                  className={`cursor-pointer p-3 rounded-lg border-2 transition-all ${
                    anniversaryType === type.id 
                      ? 'border-love-500 bg-love-50 dark:bg-love-900/30 dark:border-love-400 shadow-sm' 
                      : 'border-gray-200 dark:border-gray-700 hover:border-love-300 dark:hover:border-love-700'
                  }`}
                  whileHover={{ scale: 1.03 }}
                  whileTap={{ scale: 0.98 }}
                  aria-label={language === 'zh' ? type.nameZh : type.nameEn}
                  role="button"
                  tabIndex={0}
                >
                  <div className="flex flex-col items-center text-center">
                    <span className="emoji text-2xl mb-1">{type.icon}</span>
                    <span className={`text-sm font-medium ${
                      anniversaryType === type.id 
                        ? 'text-love-700 dark:text-love-300' 
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {language === 'zh' ? type.nameZh : type.nameEn}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Description of selected type */}
            <AnimatePresence mode="wait">
              <motion.div 
                key={anniversaryType}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="text-xs text-gray-500 dark:text-gray-400 mt-2 bg-gray-50 dark:bg-gray-800 p-2 rounded-lg"
              >
                <div className="flex items-start">
                  <SafeIcon icon={FiIcons.FiInfo} className="mr-1 mt-0.5 flex-shrink-0 text-love-400" />
                  <span>
                    {language === 'zh' 
                      ? anniversaryTypes.find(t => t.id === anniversaryType)?.descriptionZh 
                      : anniversaryTypes.find(t => t.id === anniversaryType)?.descriptionEn}
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <AnimatePresence>
            {anniversaryType === 'custom' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="bg-white dark:bg-gray-750 p-4 rounded-lg border border-love-100 dark:border-love-900 shadow-sm"
              >
                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300 flex items-center">
                  <span className="emoji mr-1">✨</span>
                  {language === 'zh' ? '自定義標籤' : 'Custom Label'}
                </label>
                <input
                  type="text"
                  value={language === 'zh' ? customLabel : customLabelEn}
                  onChange={(e) => language === 'zh' ? setCustomLabel(e.target.value) : setCustomLabelEn(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg border-2 border-love-300 dark:border-love-600 bg-white dark:bg-gray-700 text-base focus:outline-none focus:ring-2 focus:ring-love-500 dark:focus:ring-love-400 transition-colors"
                  placeholder={language === 'zh' ? '例如：第一次洗澡、第一次看獸醫...' : 'e.g. First Bath, First Vet Visit...'}
                  aria-label={language === 'zh' ? '自定義標籤' : 'Custom Label'}
                />
              </motion.div>
            )}
          </AnimatePresence>

          <button
            onClick={() => handleCalculate()}
            disabled={!anniversaryDate}
            className="w-full bg-gradient-to-r from-love-500 to-love-600 hover:from-love-600 hover:to-love-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-medium py-3 px-4 rounded-lg transition shadow-sm hover:shadow-md flex items-center justify-center"
            aria-label={language === 'zh' ? '計算我們的時光' : 'Calculate Our Time'}
          >
            <span className="emoji mr-2">{getPetTypeIcon(petType)}</span>
            {language === 'zh' ? '計算寵物紀念日' : 'Calculate Pet Anniversary'}
          </button>
        </motion.div>

        {/* Results Display */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <LoveResults
            language={language}
            calculatedDays={calculatedDays}
            detailedTime={detailedTime}
            relationshipLabel={getRelationshipLabel()}
            quote={getRandomQuote()}
            resultsVisible={resultsVisible}
            anniversaryDate={anniversaryDate}
            anniversaryType={anniversaryType}
            petType={petType}
            petName={petName}
          />
        </motion.div>
      </div>

      {/* Always show these sections, but collapsed if results aren't visible */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
        className="mt-6"
      >
        {/* Achievements Section */}
        <CollapsibleSection 
          title={language === 'zh' ? '寵物成就' : 'Pet Achievements'} 
          emoji="🏆" 
          language={language}
          isInitiallyExpanded={resultsVisible}
        >
          <AchievementsDisplay
            achievements={achievements}
            calculatedDays={calculatedDays}
            language={language}
          />
        </CollapsibleSection>

        {/* Milestones Section - Always show MilestonesDisplay */}
        <CollapsibleSection 
          title={language === 'zh' ? '寵物里程碑' : 'Pet Milestones'} 
          emoji="🎊" 
          language={language}
          isInitiallyExpanded={resultsVisible}
        >
          <MilestonesDisplay
            milestones={PET_MILESTONES}
            language={language}
            calculatedDays={calculatedDays}
            anniversaryDate={anniversaryDate}
          />
        </CollapsibleSection>
      </motion.div>
    </div>
  );
};

export default PetCalculator;